import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, Phone, Clock } from "@phosphor-icons/react";

const COLORS = ["#A7F3D0", "#DDD6FE", "#FFDDC1", "#FEF08A", "#FCA5A5", "#BFDBFE", "#FBCFE8"];

export default function Workers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [workers, setWorkers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    hours_per_week: 40,
    default_shift: "",
    color: COLORS[0],
  });

  const load = () => {
    api.get("/workers").then((r) => setWorkers(r.data));
    api.get("/shifts").then((r) => setShifts(r.data));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workers", { ...form, hours_per_week: Number(form.hours_per_week) });
      toast.success("Person added");
      setOpen(false);
      setForm({ name: "", phone: "", hours_per_week: 40, default_shift: "", color: COLORS[0] });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/workers/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="workers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Team</p>
          <h1 className="font-display text-4xl font-black tracking-tight">People & hours</h1>
        </div>
        <button
          onClick={() => setOpen(!open)}
          data-testid="add-worker-toggle"
          className={`bg-blue-500 text-white border-2 border-black rounded-lg px-5 py-3 font-bold brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center gap-2 ${
            isAdmin ? "" : "hidden"
          }`}
        >
          <Plus weight="bold" size={20} /> Add person
        </button>
      </div>

      {open && isAdmin && (
        <form
          onSubmit={save}
          data-testid="worker-form"
          className="bg-white border-2 border-black rounded-xl brutal-shadow p-6 grid sm:grid-cols-2 gap-4"
        >
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Name</label>
            <input
              required
              data-testid="worker-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-4 py-2.5 font-medium focus:outline-none focus:brutal-shadow"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Phone</label>
            <input
              data-testid="worker-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-4 py-2.5 font-medium focus:outline-none focus:brutal-shadow"
              placeholder="+1 555 000"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Hours / week</label>
            <input
              type="number"
              data-testid="worker-hours"
              value={form.hours_per_week}
              onChange={(e) => setForm({ ...form, hours_per_week: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-4 py-2.5 font-medium focus:outline-none focus:brutal-shadow"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Default shift</label>
            <select
              data-testid="worker-default-shift"
              value={form.default_shift}
              onChange={(e) => setForm({ ...form, default_shift: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-4 py-2.5 font-medium bg-white focus:outline-none"
            >
              <option value="">None</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.start_time}–{s.end_time})
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest">Colour tag</label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-9 h-9 rounded-lg border-2 border-black ${
                    form.color === c ? "ring-2 ring-offset-2 ring-blue-500" : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button
              type="submit"
              data-testid="worker-save"
              className="bg-emerald-300 border-2 border-black rounded-lg px-6 py-2.5 font-bold brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
            >
              Save person
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="bg-white border-2 border-black rounded-lg px-6 py-2.5 font-bold"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {workers.length === 0 ? (
        <div className="bg-white border-2 border-black rounded-xl brutal-shadow p-12 text-center">
          <UsersThree weight="bold" size={48} className="mx-auto mb-3" />
          <p className="font-bold text-lg">No people yet</p>
          <p className="font-medium text-slate-500">Add your team members to start planning.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {workers.map((w) => {
            const shift = shifts.find((s) => s.id === w.default_shift);
            return (
              <div
                key={w.id}
                data-testid={`worker-card-${w.id}`}
                className="bg-white border-2 border-black rounded-xl brutal-shadow p-5"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-14 h-14 rounded-xl border-2 border-black flex items-center justify-center font-display font-black text-2xl"
                    style={{ backgroundColor: w.color }}
                  >
                    {w.name?.[0]?.toUpperCase()}
                  </div>
                  <button
                    onClick={() => remove(w.id)}
                    data-testid={`worker-delete-${w.id}`}
                    className={`bg-red-300 border-2 border-black rounded-lg p-2 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all ${
                      isAdmin ? "" : "hidden"
                    }`}
                  >
                    <Trash weight="bold" size={16} />
                  </button>
                </div>
                <h3 className="font-display text-xl font-bold mt-3">{w.name}</h3>
                <div className="mt-2 space-y-1 text-sm font-medium text-slate-600">
                  <div className="flex items-center gap-2">
                    <Clock weight="bold" size={16} /> {w.hours_per_week || 0}h / week
                  </div>
                  {w.phone && (
                    <div className="flex items-center gap-2">
                      <Phone weight="bold" size={16} /> {w.phone}
                    </div>
                  )}
                </div>
                {shift && (
                  <div
                    className="mt-3 inline-block border-2 border-black rounded-md px-3 py-1 text-sm font-bold brutal-shadow-sm"
                    style={{ backgroundColor: shift.color }}
                  >
                    {shift.name} · {shift.start_time}–{shift.end_time}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
